import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

const Modal = ({ isOpen, onClose, title, children, footer, type = 'info', maxWidth = 'max-w-3xl', variant = 'default', header = null }) => {
    // Close on ESC
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const typeStyles = {
        danger: {
            bg: 'bg-rose-600',
            iconBg: 'bg-white/20 text-white',
            accent: 'border-rose-700',
            icon: 'fas fa-trash-alt'
        },
        info: {
            bg: 'bg-[#18216D]',
            iconBg: 'bg-[#FFC425] text-[#18216D]',
            accent: 'border-[#FFC425]',
            icon: 'fas fa-info-circle'
        },
        success: {
            bg: 'bg-emerald-600',
            iconBg: 'bg-white/20 text-white',
            accent: 'border-emerald-700',
            icon: 'fas fa-check-circle'
        },
        outcome: {
            bg: 'bg-white',
            iconBg: 'bg-indigo-50 text-indigo-500',
            accent: 'border-slate-100',
            icon: 'fas fa-bullseye'
        },
        experience: {
            bg: 'bg-white',
            iconBg: 'bg-emerald-50 text-emerald-500',
            accent: 'border-slate-100',
            icon: 'fas fa-walking'
        },
        'inquiry-question': {
            bg: 'bg-white',
            iconBg: 'bg-amber-50 text-amber-500',
            accent: 'border-slate-100',
            icon: 'fas fa-question-circle'
        },
        strand: {
            bg: 'bg-white',
            iconBg: 'bg-blue-50 text-blue-500',
            accent: 'border-slate-100',
            icon: 'fas fa-layer-group'
        },
        'sub-strand': {
            bg: 'bg-white',
            iconBg: 'bg-cyan-50 text-cyan-500',
            accent: 'border-slate-100',
            icon: 'fas fa-stream'
        }
    };

    const currentStyle = typeStyles[type] || typeStyles.info;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-8">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-500"
                onClick={onClose}
            ></div>

            {/* Modal Body */}
            <div className={`relative bg-white rounded-3xl shadow-[0_40px_120px_-30px_rgba(24,33,109,0.5)] w-full ${maxWidth} max-h-[90vh] overflow-y-auto overflow-x-hidden custom-scrollbar border border-white/20 animate-in zoom-in-95 duration-500 flex flex-col`}>
                
                {/* Header Container */}
                {header ? (
                    header
                ) : variant === 'compact' ? (
                    <div className="relative bg-white px-7 py-5 flex items-center justify-between z-10 border-b border-slate-100 flex-shrink-0">
                        <div className="flex items-center gap-3.5">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${currentStyle.iconBg} flex-shrink-0`}>
                                <i className={`${currentStyle.icon} text-base`}></i>
                            </div>
                            <div>
                                <h3 className="text-base sm:text-lg font-black text-[#18216D] tracking-tight uppercase italic leading-snug">
                                    {title}
                                </h3>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-9 h-9 rounded-xl bg-slate-50 hover:bg-rose-50 text-slate-400 hover:text-rose-500 flex items-center justify-center transition-all group flex-shrink-0 ml-3"
                        >
                            <XMarkIcon className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                        </button>
                    </div>
                ) : (
                    <div className={`relative ${currentStyle.bg} px-7 py-5 border-b-2 ${currentStyle.accent} flex items-center justify-between z-10 shadow-md flex-shrink-0`}>
                        <div className="flex items-center gap-3.5">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-inner ${currentStyle.iconBg} flex-shrink-0`}>
                                <i className={`${currentStyle.icon} text-base`}></i>
                            </div>
                            <div>
                                <h3 className="text-base sm:text-lg font-black text-white tracking-tight uppercase Montserrat-Black leading-snug">
                                    {title}
                                </h3>
                            </div>
                        </div>
                        
                        <button
                            onClick={onClose}
                            className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all group flex-shrink-0 ml-3"
                        >
                            <XMarkIcon className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                        </button>
                        
                        {/* Decorative Background Elements */}
                        <div className="absolute top-0 right-0 w-36 h-36 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl pointer-events-none"></div>
                    </div>
                )}

                {/* Content Area */}
                <div className="p-7 bg-white flex-1 overflow-y-auto">
                    <div className="text-slate-600 font-medium leading-relaxed italic Montserrat-Medium h-full">
                        {children}
                    </div>
                </div>

                {/* Footer Section */}
                {footer && (
                    <div className="px-7 py-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-end">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default Modal;
